import styled, { css } from 'styled-components';
import { IconChevronDown } from '@u21/tabler-icons';
import {
  U21Button,
  U21ButtonProps,
} from 'app/shared/u21-ui/components/input/U21Button';
import { ToggleButtonGroup } from '@mui/material';
import {
  U21MenuButton,
  U21MenuButtonItems,
  U21MenuButtonProps,
} from 'app/shared/u21-ui/components/display/U21MenuButton';
import { U21MenuItemProps } from 'app/shared/u21-ui/components/display/U21MenuItem';
import { getDOMProps } from 'app/shared/utils/react';
import { ReactElement, useMemo } from 'react';

export interface U21SplitButtonProps extends Omit<
  U21ButtonProps,
  'icon' | 'variant'
> {
  alignRight?: boolean;
  items: U21MenuButtonItems;
  menuButtonProps?: Omit<
    U21MenuButtonProps,
    'alignRight' | 'buttonProps' | 'children' | 'items' | 'showArrows'
  >;
  variant?: Exclude<U21ButtonProps['variant'], 'text'>;
}

export const U21SplitButton = ({
  alignRight,
  children,
  color = 'inherit',
  disabled,
  disableTitleCase,
  endIcon,
  form,
  href,
  items,
  loading,
  menuButtonProps,
  onClick,
  size,
  startIcon,
  to,
  tooltip,
  tooltipProps,
  type,
  variant,
  ...rest
}: U21SplitButtonProps) => {
  const itemsToRender = useMemo(
    () => items.filter((i): i is U21MenuItemProps | ReactElement => Boolean(i)),
    [items],
  );

  return (
    <ToggleButtonGroup {...getDOMProps(rest)}>
      <U21Button
        className={
          itemsToRender.length > 0 ? 'MuiToggleButtonGroup-firstButton' : ''
        }
        color={color}
        disabled={disabled}
        disableTitleCase={disableTitleCase}
        form={form}
        href={href}
        endIcon={endIcon}
        loading={loading}
        onClick={onClick}
        size={size}
        startIcon={startIcon}
        to={to}
        tooltip={tooltip}
        tooltipProps={tooltipProps}
        type={type}
        variant={variant}
      >
        {children}
      </U21Button>
      {itemsToRender.length > 0 && (
        <StyledU21MenuButton
          alignRight={alignRight}
          aria-label="More options"
          buttonProps={{
            color,
            disabled: disabled || loading,
            size,
            variant,
          }}
          className="MuiToggleButtonGroup-lastButton"
          items={items}
          {...menuButtonProps}
        >
          <IconChevronDown />
        </StyledU21MenuButton>
      )}
    </ToggleButtonGroup>
  );
};

const StyledU21MenuButton = styled(U21MenuButton)`
  padding: 0 4px;

  ${(props) => {
    if (props.buttonProps?.variant === 'contained') {
      const { color = 'inherit' } = props.buttonProps;
      return css`
        &.MuiToggleButtonGroup-lastButton {
          border-left: 1px solid
            ${color === 'inherit'
              ? props.theme.palette.grey[500_32]
              : props.theme.palette[color].dark};
        }
      `;
    }
    return css``;
  }}
`;
